import { dao } from '@platform/dao';
import type { IPlatformUser, IRolePermissions } from '@platform/dao';
import { baseApi, run } from './baseApi';

const rolesTag = (ws: string) => ({ type: 'Roles' as const, id: ws });
const platformTag = { type: 'Settings' as const, id: 'platform' };

/**
 * Адміністрування (прототип v4): «Ролі й дозволи» простору (матриця, зміна клітинки, повернення до типової), налаштування платформи
 * («Загальне», «Способи входу», глобальне «Розміщення полів») і глобальні ролі облікових записів. Відповіді змін — новий стан, тож кеш
 * оновлюється відповіддю без повторного запиту.
 */
export const adminApi = baseApi.injectEndpoints({
  endpoints: (b) => ({
    rolePermissions: b.query<IRolePermissions, string>({ queryFn: (ws) => run(() => dao.workspaces.rolePermissions(ws)), providesTags: (_r, _e, ws) => [rolesTag(ws)] }),
    setRolePermission: b.mutation<IRolePermissions, { ws: string; role: string; capability: string; allowed: boolean }>({
      queryFn: ({ ws, role, capability, allowed }) => run(() => dao.workspaces.setRolePermission(ws, role, capability, allowed)),
      invalidatesTags: (_r, _e, { ws }) => [rolesTag(ws)],
    }),
    resetRolePermissions: b.mutation<IRolePermissions, string>({
      queryFn: (ws) => run(() => dao.workspaces.resetRolePermissions(ws)),
      invalidatesTags: (_r, _e, ws) => [rolesTag(ws)],
    }),
    platformSettings: b.query<Record<string, unknown>, void>({ queryFn: () => run(() => dao.platform.settings()), providesTags: [platformTag] }),
    setPlatformSetting: b.mutation<Record<string, unknown>, { key: string; value: unknown }>({
      queryFn: ({ key, value }) => run(() => dao.platform.setSetting(key, value)),
      invalidatesTags: [platformTag],
    }),
    platformUsers: b.query<IPlatformUser[], string | void>({
      queryFn: (q) => run(() => dao.platform.users(q || undefined)),
      providesTags: [{ type: 'Roles', id: 'platform' }],
    }),
    setGlobalRole: b.mutation<IPlatformUser, { userId: string; globalRole: string }>({
      queryFn: ({ userId, globalRole }) => run(() => dao.platform.setGlobalRole(userId, globalRole)),
      invalidatesTags: [{ type: 'Roles', id: 'platform' }, 'Me', 'Workspaces'],
    }),
  }),
});

export const {
  useRolePermissionsQuery, useSetRolePermissionMutation, useResetRolePermissionsMutation, usePlatformSettingsQuery, useSetPlatformSettingMutation,
  usePlatformUsersQuery, useSetGlobalRoleMutation,
} = adminApi;
